// ACHIEVEMENTS page (title menu → ACHIEVEMENTS): tabs ACHIEVEMENTS / QUESTS / CHALLENGES (LB / RB or [ ]), a list on the
// left and a detail panel on the right (stacked on phones). Keyboard (arrows / WASD), mouse, touch and gamepad, like the
// Encyclopedia (codexMenu.ts), whose tile / detail styles it reuses so all five menu styles apply. Data: src/app/feats.ts.
//   ACHIEVEMENTS: confirm on an unlocked achievement that grants a title wears (or removes) that title.
//   CHALLENGES:   confirm (or tap the focused row again / START CHALLENGE) starts the challenge on your last-picked arena.
import { modDef, modList } from '../core/mods';
import { WEAPONS } from '../core/content';
import { badgeHtml, badgeSvg, TIER_NAME } from './badges';
import {
  ACHIEVEMENTS, ACH_BY_ID, CAT_LABEL, CHALLENGES, QUEST_BY_ID, STORY, TIME_ACH, TIME_QUEST, achProgress, dayKey, goalText, msToNextDay,
  progText, rollQuests, settle, type AchDef, type ChallengeDef, type FeatSummary, type FeatsSave, type QuestDef,
} from './feats';
import type { Codex } from './codex';
import type { UiAction } from './input';

export interface FeatsHost {
  feats(): FeatsSave;
  codex(): Codex;
  /** wallet (Shard Hoard) */
  shards(): number;
  pay(amount: number, note: string): void;
  save(): void;
  click(): void;
  deny(): void;
  close(): void;
  /** [previous, next] tab button labels for the current device */
  tabLabels(): [string, string];
  /** name of the arena a challenge would start on */
  arena(): string;
  startChallenge(id: string): void;
}

const $ = <T extends HTMLElement>(s: string) => document.querySelector(s) as T;
const TABS = ['ACHIEVEMENTS', 'QUESTS', 'CHALLENGES'] as const;
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const bar = (cur: number, goal: number, done = false) => `<span class="ft-bar${done ? ' done' : ''}"><i style="width:${Math.round(Math.min(1, cur / Math.max(1, goal)) * 100)}%"></i></span>`;
const fmtDate = (t: number) => (t > 0 ? new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '');
const secretBadge = () => badgeSvg({ frame: 'secret', tier: 0, glyph: 'dots3' }, true);
const runLen = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export class FeatsMenu {
  tab = 0;
  private sel = [0, 0, 0];
  /** achievements unlocked by opening the page (Encyclopedia / Shard Hoard): shown once in the count line */
  private news: AchDef[] = [];

  constructor(private host: FeatsHost) {
    $('#ft-tabs').addEventListener('click', (e) => {
      const el = e.target as HTMLElement, t = el.closest('.stab') as HTMLElement | null, b = el.closest('.bump') as HTMLElement | null;
      if (t) this.setTab(Number(t.dataset.t)); else if (b) this.setTab(this.tab + Number(b.dataset.d));
    });
    $('#ft-list').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('[data-i]') as HTMLElement | null; if (!b) return;
      const i = Number(b.dataset.i);
      if (i === this.sel[this.tab] && this.tab !== 1) this.confirm(true); else this.select(i, true); // second tap = confirm
    });
    $('#ft-detail').addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('[data-act]') as HTMLElement | null; if (b) this.confirm();
    });
    $('#ft-back').addEventListener('click', () => this.close());
  }

  private get f(): FeatsSave { return this.host.feats(); }

  open(): void {
    rollQuests(this.f.quests, dayKey(new Date()));
    // goals met outside a run (Encyclopedia discoveries, shards held) unlock here
    this.news = settle(this.f, this.host.codex(), this.host, Date.now(), this.host.shards());
    this.host.save();
    this.render();
  }
  close(): void {
    if (this.f.fresh.length) { this.f.fresh.length = 0; this.host.save(); } // NEW badges seen
    this.news = [];
    this.host.close();
  }
  setTab(i: number): void { this.tab = (i + TABS.length) % TABS.length; this.host.click(); this.render(); }
  private count(): number { return this.tab === 0 ? ACHIEVEMENTS.length : this.tab === 1 ? this.f.quests.slots.length : CHALLENGES.length; }
  select(i: number, fromPointer = false): void {
    const n = this.count(); if (!n) return;
    const j = Math.max(0, Math.min(n - 1, i));
    if (j !== this.sel[this.tab]) this.host.click();
    this.sel[this.tab] = j; this.render(fromPointer);
  }
  private cols(): number { return this.tab === 0 ? Math.max(1, getComputedStyle($('#ft-list')).gridTemplateColumns.split(' ').filter(Boolean).length) : 1; }

  handle(a: UiAction): void {
    const n = this.count(), i = this.sel[this.tab];
    if (a === 'back' || a === 'pause') return this.close();
    if (a === 'tabPrev') return this.setTab(this.tab - 1);
    if (a === 'tabNext') return this.setTab(this.tab + 1);
    if (a === 'confirm') return this.confirm();
    if (!n) return;
    const c = this.cols();
    if (a === 'right') this.select(c === 1 ? i : (i + 1) % n);
    else if (a === 'left') this.select(c === 1 ? i : (i + n - 1) % n);
    else if (a === 'down') this.select(c === 1 ? (i + 1) % n : i + c < n ? i + c : i);
    else if (a === 'up') this.select(c === 1 ? (i + n - 1) % n : i - c >= 0 ? i - c : i);
  }

  /** confirm on the focused entry: wear a title / start a challenge */
  confirm(fromPointer = false): void {
    if (this.tab === 0) {
      const a = ACHIEVEMENTS[this.sel[0]], f = this.f;
      if (!a?.titleReward || f.unlocked[a.id] === undefined) { if (!fromPointer) this.host.deny(); return; }
      f.title = f.title === a.id ? '' : a.id; this.host.click(); this.host.save(); this.render(); return;
    }
    if (this.tab === 2) { const c = CHALLENGES[this.sel[2]]; if (c) { this.host.click(); this.host.startChallenge(c.id); } }
  }

  render(fromPointer = false): void {
    const f = this.f, [bl, br] = this.host.tabLabels();
    const got = ACHIEVEMENTS.filter((a) => f.unlocked[a.id] !== undefined).length;
    const clr = CHALLENGES.filter((c) => (f.challenges[c.id]?.done ?? 0) > 0).length;
    const tabSub = [`${got}/${ACHIEVEMENTS.length}`, `${f.quests.done} done`, `${clr}/${CHALLENGES.length}`];
    $('#ft-tabs').innerHTML = `<b class="bump" data-d="-1">◀ ${bl}</b>`
      + TABS.map((t, k) => `<span class="stab${k === this.tab ? ' on' : ''}" data-t="${k}">${t} <small>${tabSub[k]}</small></span>`).join('')
      + `<b class="bump" data-d="1">${br} ▶</b>`;
    const title = f.title && ACH_BY_ID[f.title]?.titleReward;
    const news = this.news.length ? ` · <em>New: ${this.news.map((a) => esc(a.title)).join(', ')} (+${this.news.reduce((s, a) => s + a.reward, 0)} ◆)</em>` : '';
    $('#ft-count').innerHTML = `<b>${got} / ${ACHIEVEMENTS.length}</b> unlocked · <b>${f.earned.toLocaleString('en-US')} ◆</b> earned${title ? ` · Title: <b>${esc(title)}</b>` : ''}${news}`;
    $('#ft-list').className = ['ft-grid', 'ft-rows', 'ft-rows'][this.tab];
    if (this.tab === 0) this.renderAch(); else if (this.tab === 1) this.renderQuests(); else this.renderChallenges();
    const foc = document.querySelector<HTMLElement>('#ft-list .focus');
    if (foc && !fromPointer) foc.scrollIntoView({ block: 'nearest' });
    $('#ft-detail').scrollTop = 0;
  }

  // ---------------------------------------------------------------------------------------------------------- ACHIEVEMENTS
  private renderAch(): void {
    const f = this.f, codex = this.host.codex(), sel = Math.min(this.sel[0], ACHIEVEMENTS.length - 1);
    $('#ft-list').innerHTML = ACHIEVEMENTS.map((a, i) => {
      const p = achProgress(a, f, codex), secret = a.hidden && !p.done;
      return `<button class="cx-tile ft-ach${p.done ? '' : ' locked'}${i === sel ? ' focus' : ''}" data-i="${i}" aria-label="${secret ? 'Secret achievement' : esc(a.title)}">`
        + `<span class="cx-pic ft-pic">${secret ? secretBadge() : badgeHtml(a.id, a.badge, !p.done)}</span><span class="cx-nm">${secret ? '???' : esc(a.title)}</span>`
        + (p.done ? '<span class="ft-ok">✓</span>' : secret ? '' : bar(p.cur, p.goal))
        + (f.fresh.includes(a.id) ? '<span class="cx-new">NEW</span>' : '') + '</button>';
    }).join('');
    const a = ACHIEVEMENTS[sel]; if (!a) return;
    const p = achProgress(a, f, codex), secret = a.hidden && !p.done;
    const tags = `<span>${CAT_LABEL[a.cat]}</span>${secret ? '<span>SECRET</span>' : `<span class="ft-t${a.badge.tier}">${TIER_NAME[a.badge.tier].toUpperCase()}</span>`}${p.done ? '<span class="t-evo">✓ UNLOCKED</span>' : '<span>LOCKED</span>'}`;
    let html = `<div class="cx-head"><div class="cx-art ft-big${p.done ? '' : ' cx-dim'}">${secret ? secretBadge() : badgeHtml(a.id, a.badge, !p.done)}</div><div class="cx-ttl"><h3>${secret ? '???' : esc(a.title)}</h3><div class="cx-tags">${tags}</div></div></div>`;
    html += `<p class="cx-desc">${secret ? 'A secret achievement. Keep playing: it unlocks when you stumble on it.' : esc(a.desc)}</p>`;
    if (!secret) html += `<div class="ft-prog">${bar(p.cur, p.goal, p.done)}<span>${p.goal > 1 ? progText(p.cur, p.goal, TIME_ACH.has(a.id)) : p.done ? 'Done' : 'Not yet'}</span></div>`;
    html += `<div class="cx-sec">REWARD</div><p class="ft-rw"><b>+${a.reward} ◆</b> Fracture Shards${a.titleReward && !secret ? ` · Title <b>${esc(a.titleReward)}</b>` : ''}</p>`;
    if (a.titleReward && p.done) {
      const worn = this.f.title === a.id;
      html += `<button class="btn ft-act" data-act="title">${worn ? 'REMOVE TITLE' : 'WEAR TITLE'}</button>`;
    }
    if (p.done) html += `<div class="cx-rec">Unlocked <b>${fmtDate(f.unlocked[a.id]) || 'yes'}</b></div>`;
    $('#ft-detail').innerHTML = html;
  }

  // ---------------------------------------------------------------------------------------------------------- QUESTS
  private renderQuests(): void {
    const f = this.f, qs = f.quests, sel = Math.min(this.sel[1], qs.slots.length - 1);
    $('#ft-list').innerHTML = qs.slots.map((s, i) => {
      const d = QUEST_BY_ID[s.id]; if (!d) return '';
      const cur = Math.min(d.goal, s.prog);
      return `<div class="ft-row${i === sel ? ' focus' : ''}" data-i="${i}" role="button" tabindex="-1">`
        + `<span class="ft-kind ${d.kind}">${d.kind === 'story' ? 'STORY' : 'DAILY'}</span>`
        + `<span class="ft-rt"><b>${esc(d.title)}</b><small>${esc(d.desc)}</small>${bar(cur, d.goal)}<small class="ft-pt">${progText(cur, d.goal, TIME_QUEST(d))}</small></span>`
        + `<span class="ft-rw">+${d.reward} ◆</span></div>`;
    }).join('') + `<p class="ft-foot">New daily quests in <b>${this.untilTomorrow()}</b> · ${qs.done} quest${qs.done === 1 ? '' : 's'} completed</p>`;
    const s = qs.slots[sel], d = s && QUEST_BY_ID[s.id]; if (!d) { $('#ft-detail').innerHTML = ''; return; }
    let html = `<div class="cx-head"><div class="cx-ttl"><h3>${esc(d.title)}</h3><div class="cx-tags"><span>${d.kind === 'story' ? 'STORY QUEST' : 'DAILY QUEST'}</span><span>${d.scope === 'run' ? 'IN ONE RUN' : 'ACROSS RUNS'}</span></div></div></div>`
      + `<p class="cx-desc">${esc(d.desc)}</p><div class="ft-prog">${bar(Math.min(d.goal, s.prog), d.goal)}<span>${progText(Math.min(d.goal, s.prog), d.goal, TIME_QUEST(d))}</span></div>`
      + `<div class="cx-sec">REWARD</div><p class="ft-rw"><b>+${d.reward} ◆</b> Fracture Shards</p>`;
    if (d.kind === 'story') html += `<div class="cx-sec">STORY <small>${Math.min(qs.story, STORY.length)} / ${STORY.length}</small></div><ol class="ft-chain">${STORY.map((q: QuestDef, k) => `<li class="${k < qs.story ? 'ok' : k === qs.story ? 'now' : ''}">${k <= qs.story ? esc(q.title) : '???'}</li>`).join('')}</ol>`;
    else html += `<p class="cx-note">Daily quests follow your device's date: two new ones every day. Finish one and another takes its place. Training runs don't count.</p>`;
    $('#ft-detail').innerHTML = html;
  }
  private untilTomorrow(): string { const ms = msToNextDay(new Date()), h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4); return h > 0 ? `${h}h ${m}m` : `${m}m`; }

  // ---------------------------------------------------------------------------------------------------------- CHALLENGES
  private renderChallenges(): void {
    const f = this.f, sel = Math.min(this.sel[2], CHALLENGES.length - 1);
    $('#ft-list').innerHTML = CHALLENGES.map((c, i) => {
      const r = f.challenges[c.id], done = (r?.done ?? 0) > 0;
      return `<div class="ft-row ch${i === sel ? ' focus' : ''}${done ? ' done' : ''}" data-i="${i}" role="button" tabindex="-1">`
        + `<span class="ft-cb${done ? '' : ' cx-dim'}">${badgeHtml(`ch_${c.id}`, c.badge, !done)}</span>`
        + `<span class="ft-rt"><b>${esc(c.name)}</b><small>${esc(goalText(c.goal))} · ${this.rulesShort(c)}</small></span>`
        + `<span class="ft-rw">${done ? '✓ CLEARED' : `+${c.reward} ◆`}</span></div>`;
    }).join('');
    const c = CHALLENGES[sel]; if (!c) return;
    const r = f.challenges[c.id], done = (r?.done ?? 0) > 0;
    let html = `<div class="cx-head"><div class="cx-art ft-big${done ? '' : ' cx-dim'}">${badgeHtml(`ch_${c.id}`, c.badge, !done)}</div><div class="cx-ttl"><h3>${esc(c.name)}</h3><div class="cx-tags"><span>CHALLENGE</span>${done ? '<span class="t-evo">✓ CLEARED</span>' : ''}</div></div></div>`
      + `<p class="cx-desc">${esc(c.desc)}</p>`
      + `<div class="cx-sec">GOAL</div><p class="ft-rw"><b>${esc(goalText(c.goal))}</b> on a ${runLen(c.bossTime)} run</p>`
      + `<div class="cx-sec">RULES</div><ul class="cx-notes">${this.rulesLong(c).map((x) => `<li>${x}</li>`).join('')}</ul>`
      + `<div class="cx-sec">REWARD</div><p class="ft-rw"><b>+${c.reward} ◆</b> on the first clear, and its badge</p>`
      + `<button class="btn primary ft-act" data-act="start">START CHALLENGE ▶</button>`
      + `<div class="cx-rec">Arena: <b>${esc(this.host.arena())}</b> (your last pick) · Tries <b>${r?.tries ?? 0}</b>${c.goal.kind === 'survive' && r ? ` · Best <b>${Math.round(r.best * 100)}%</b>` : ''}${done ? ` · Cleared <b>${r!.done}×</b>${r!.first ? ` (first ${fmtDate(r!.first)})` : ''}` : ''}</div>`;
    $('#ft-detail').innerHTML = html;
  }
  private rulesShort(c: ChallengeDef): string {
    const out = modList(c.mods).map((id) => modDef(id).name);
    if (c.rules?.skillSlots) out.push(`${c.rules.skillSlots} skill slot${c.rules.skillSlots === 1 ? '' : 's'}`);
    if (c.rules?.startSkills) out.push(`+${c.rules.startSkills} random skills`);
    if (c.noPowerUps) out.push('No Power Ups');
    return esc(out.join(' · '));
  }
  private rulesLong(c: ChallengeDef): string[] {
    const out = modList(c.mods).map((id) => `<b>${modDef(id).name}</b>: ${esc(modDef(id).desc)}`);
    if (c.rules?.skillSlots) out.push(`<b>${c.rules.skillSlots} skill slot${c.rules.skillSlots === 1 ? '' : 's'}</b>: ${c.rules.skillSlots === 1 ? `only ${WEAPONS.bolt.name}` : `${WEAPONS.bolt.name} plus ${c.rules.skillSlots - 1} more`} (traits are not limited).`);
    if (c.rules?.startSkills) out.push(`<b>Start with ${c.rules.startSkills} random skills</b> on top of ${WEAPONS.bolt.name}.`);
    if (c.noPowerUps) out.push('<b>Power Ups off</b>: your Workshop ranks are ignored.');
    out.push('Fixed rules: this challenge uses its own time limit and ignores your Run Setup modifiers. Game speed and other options stay as set.');
    return out;
  }
}

/** results-screen block: what this run unlocked and advanced */
export function featSummaryHtml(s: FeatSummary | null): string {
  if (!s) return '';
  const rows: string[] = [];
  if (s.challenge) rows.push(s.challenge.done
    ? `<div class="ofs ch ok"><b>CHALLENGE CLEARED · ${esc(s.challenge.def.name)}</b>${s.challenge.first ? ` <span>+${s.challenge.def.reward} ◆</span>` : ''}</div>`
    : `<div class="ofs ch"><b>CHALLENGE · ${esc(s.challenge.def.name)}</b> <span>not cleared (${esc(goalText(s.challenge.def.goal))})</span></div>`);
  for (const a of s.unlocked) rows.push(`<div class="ofs ach"><span class="ofs-b">${badgeHtml(a.id, a.badge)}</span><b>${esc(a.title)}</b> <span>+${a.reward} ◆</span></div>`);
  for (const q of s.quests) {
    if (!q.done && q.gained <= 0) continue;
    rows.push(`<div class="ofs q${q.done ? ' ok' : ''}"><b>${q.done ? 'QUEST DONE · ' : ''}${esc(q.def.title)}</b>${q.done ? ` <span>+${q.def.reward} ◆</span>` : ` ${bar(q.prog, q.goal)} <span>${progText(q.prog, q.goal, TIME_QUEST(q.def))}</span>`}</div>`);
  }
  if (!rows.length) return '';
  return `<div class="ofs-h">PROGRESS${s.shards > 0 ? ` · +${s.shards} ◆ from achievements, quests and challenges` : ''}</div>${rows.slice(0, 7).join('')}${rows.length > 7 ? `<div class="ofs more">+${rows.length - 7} more</div>` : ''}`;
}
